import { InfoIcon, SealQuestionIcon, WarningCircleIcon, WarningIcon } from '@phosphor-icons/react';
import { useId, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { attentionParams, weakMarketReasons } from './analytics';
import { ProvenanceInfo } from './ProvenanceInfo';
import type { AttentionFlagData, AttentionSeverity, VehicleSource } from './types';
import styles from './AttentionFlags.module.css';

interface AttentionFlagsProps {
  vehicleId: string;
  currency: string;
  /** Порядок приходит с backend (`attention.domain.DISPLAY_ORDER`) и не меняется. */
  flags: AttentionFlagData[];
  source?: VehicleSource | null | undefined;
}

const SEVERITY_ICONS: Record<AttentionSeverity, ReactNode> = {
  info: <InfoIcon size={18} weight="bold" aria-hidden="true" />,
  requires_attention: <WarningIcon size={18} weight="fill" aria-hidden="true" />,
  significant_uncertainty: <SealQuestionIcon size={18} weight="fill" aria-hidden="true" />,
};

/**
 * Признаки, требующие внимания (FE-061, MVP_PRD §15).
 *
 * Список наблюдений над объявлением с тремя уровнями значимости. Тексты берутся
 * из i18n по коду правила: backend присылает код и основание, формулировок не
 * пишет — поэтому «пробег скручен» не может появиться здесь ни при каких данных.
 *
 * Три вещи, которые держит именно этот компонент:
 *
 * * **признак — не обвинение.** Заголовок говорит о наблюдении, подпись — о
 *   том, что его нужно проверить отдельно. Формулировок о дефектах, авариях и
 *   недобросовестности продавца в словарях нет;
 * * **уровень значимости несёт не только цвет.** У каждого уровня своя иконка и
 *   текстовая подпись — цвет никогда не единственный носитель смысла (§25);
 * * **пустого блока не бывает.** Когда не сработало ни одно правило, блок не
 *   рисуется вовсе: писать «замечаний нет» нельзя (это утверждение о состоянии
 *   автомобиля), а объяснять пользователю, какие правила прогонялись и почему
 *   промолчали, — отладочная информация, а не интерфейс.
 */
export function AttentionFlags({ vehicleId, currency, flags, source }: AttentionFlagsProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'de';
  // См. MarketComparison: идентификатор генерируется ради страниц с несколькими
  // автомобилями, где такой блок появится не один раз.
  const titleId = useId();

  if (flags.length === 0) return null;

  return (
    <section className={styles.shell} aria-labelledby={titleId}>
      <div className={styles.core}>
        <header className={styles.header}>
          <div className={styles.heading}>
            <span className={styles.headerIcon} aria-hidden="true">
              <WarningCircleIcon size={18} weight="bold" />
            </span>
            <div>
              <h2 id={titleId} className={styles.title}>
                {t('vehicle.attention.title')}
              </h2>
              <p className={styles.count}>
                {t('vehicle.attention.count', { count: flags.length })}
              </p>
            </div>
          </div>
          <ProvenanceInfo
            vehicleId={vehicleId}
            subject={t('vehicle.attention.title')}
            {...(source ? { source: { name: source.name, url: source.url } } : {})}
          />
        </header>

        <ul className={styles.list}>
          {flags.map((flag) => (
            <li key={flag.code}>
              <AttentionItem flag={flag} locale={locale} currency={currency} />
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function AttentionItem({
  flag,
  locale,
  currency,
}: {
  flag: AttentionFlagData;
  locale: string;
  currency: string;
}) {
  const { t } = useTranslation();
  const params = attentionParams(flag, { locale, currency });
  const reasons = weakMarketReasons(flag);

  // Основания не хватило — остаётся только заголовок. Формулировка с дырой
  // вместо величины была бы выводом без evidence (MVP_PRD §14).
  const text =
    params === null
      ? null
      : t(`vehicle.attention.text.${flag.code}`, { ...params, defaultValue: '' });

  return (
    <article className={[styles.item, styles[flag.severity]].filter(Boolean).join(' ')}>
      <span className={styles.itemIcon}>{SEVERITY_ICONS[flag.severity]}</span>
      <div className={styles.itemBody}>
        <p className={styles.itemTitle}>
          {t(`vehicle.attention.code.${flag.code}`, { defaultValue: flag.code })}
        </p>
        {text ? <p className={styles.itemText}>{text}</p> : null}

        {reasons.length > 0 ? (
          <ul className={styles.reasons}>
            {reasons.map((reason) => (
              <li key={reason}>{t(`vehicle.attention.reason.${reason}`)}</li>
            ))}
          </ul>
        ) : null}

        <p className={styles.itemSeverity}>{t(`vehicle.attention.severity.${flag.severity}`)}</p>
      </div>
    </article>
  );
}
